import React, { useState } from "react"
import { SubscribeRelendPayload, subscribeRelendRequest } from "@/apis/lendingRequests"
import { HelperText } from "@/utils/dataTypes"
import { Container, Stack, Typography } from "@mui/material"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import validator from "validator"
import Button from "@/components/base/Button"
import InputBase from "@mui/material/InputBase"
import HomeLineBackground from "@/assets/images/home-line-bg.png"

const JoinWithUs = () => {
  const trans = useTrans()
  const { address } = useWalletConnect()
  const [emailAddress, setEmailAddress] = useState("")
  const [isValid, setIsValid] = useState(true)
  const [helperText, setHelperText] = useState<HelperText>({
    content: "",
    color: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = event.target.value
    setEmailAddress(newEmail)
    setHelperText({
      content: "",
      color: "",
    })
    setIsValid(true)
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    const isValid = validator.isEmail(emailAddress)
    if (!isValid) {
      setIsValid(isValid)
      setHelperText({ content: trans.home.invalid_email, color: "red" })
      setIsSubmitting(false)
      return
    }

    const walletAddress = address ?? ""
    const payload: SubscribeRelendPayload = {
      email_address: emailAddress,
      wallet_address: walletAddress,
      is_liquidator: false,
    }
    const result = await subscribeRelendRequest(payload)
    if (result.error) {
      setIsValid(false)

      const error = result.error
      if (error?.response?.data?.errors) {
        setHelperText({ content: error.response.data.errors[0], color: "red" })
      } else {
        setHelperText({ content: error.message, color: "red" })
      }
    } else {
      setHelperText({ content: trans.home.email_submit_success, color: "green600" })
    }

    setIsSubmitting(false)
  }

  return (
    <Container maxWidth='xl' className='mb-10 sm:mb-20'>
      <Stack
        className='bg-secondary rounded-2xl py-10 sm:py-20 px-6'
        gap={3}
        sx={{ backgroundImage: `url(${HomeLineBackground.src})`, backgroundRepeat: "repeat" }}
      >
        <Stack alignItems='center' gap={1}>
          <Typography className='text-neutral-1000 text-center font-montserrat text-2xl sm:text-[2.5rem] font-bold leading-tight w-3/4 sm:w-full'>
            {trans.home.stay_updated_with_renec_lend}
          </Typography>
          <Typography className='text-sm sm:text-base text-neutral-600 text-center sm:max-w-3xl w-3/4 sm:w-full'>
            {trans.home.join_with_your_email}
          </Typography>
        </Stack>

        <Stack gap={1} alignItems='center'>
          <Stack direction='row' gap={1} className='p-1 bg-white rounded-lg w-full max-w-md'>
            <InputBase
              placeholder={trans.home.enter_your_email}
              style={{ border: "none" }}
              inputProps={{ maxLength: 50, style: { padding: "12px 16px", border: "none" } }}
              value={emailAddress}
              onChange={handleEmailChange}
              error={Boolean(isValid)}
              data-testid='step-info-input-name'
              className='rounded-lg border border-solid border-secondary flex-1'
            />
            <Button variant='primary' size='md' onClick={handleSubmit} loading={isSubmitting}>
              {trans.home.join_now}
            </Button>
          </Stack>
          {helperText.content && <Typography className='text-red-600'>{helperText.content}</Typography>}
        </Stack>
      </Stack>
    </Container>
  )
}

export default JoinWithUs
